import { EditorContent, Editor } from "@tiptap/react";
import { useCallback } from "react";
import KodeTagDropdown from "../dropdown/KodeTagDropdown";
import Button from "@/components/bricks/button/Button";
import LinkDropdown from "../dropdown/LinkDropdown";
import FormatBoldIcon from "../icons/FormatBoldIcon";
import FormatItalicIcon from "../icons/FormatItalicIcon";
import FormatStrikethroughIcon from "../icons/FormatStrikethroughIcon";
import FormatUnderlineIcon from "../icons/FormatUnderlineIcon";
import UndoIcon from "../icons/UndoIcon";
import RedoIcon from "../icons/RedoIcon";

export default function EmailEditor({
  editor,
  emailType,
}: {
  editor: Editor;
  emailType: IEmailTypes;
}) {
  const toggleBold = useCallback(() => {
    editor.chain().focus().toggleBold().run();
  }, [editor]);

  const toggleUnderline = useCallback(() => {
    editor.chain().focus().toggleUnderline().run();
  }, [editor]);

  const toggleItalic = useCallback(() => {
    editor.chain().focus().toggleItalic().run();
  }, [editor]);

  const addCodeTag = useCallback(
    (codetag: string) => {
      const content = `{{${codetag}}}`;

      editor.commands.insertContent(content);
    },
    [editor],
  );

  const toggleStrike = useCallback(() => {
    editor.chain().focus().toggleStrike().run();
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <>
      <div
        className="l-email-toolbar btn-toolbar align-items-center"
        role="toolbar"
        aria-label="Toolbar med formateringsmuligheder"
      >
        <div
          className="btn-group rounded-0 pe-2"
          role="group"
          aria-label="Første gruppe"
        >
          <Button
            transparent
            color="secondary"
            type="button"
            className="btn--icon rounded"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            aria-label="Fortryd"
          >
            <UndoIcon />
          </Button>
          <Button
            transparent
            color="secondary"
            type="button"
            className="btn--icon rounded"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            aria-label="Gør det igen"
          >
            <RedoIcon />
          </Button>
        </div>
        <span className="separator"></span>
        <div
          className="btn-group rounded-0 px-2"
          role="group"
          aria-label="Anden gruppe"
        >
          <Button
            transparent
            type="button"
            color="secondary"
            className={`btn--icon rounded${
              editor.isActive("bold") ? " text-dark" : ""
            }`}
            onClick={toggleBold}
            aria-label="Fed"
          >
            <FormatBoldIcon />
          </Button>
          <Button
            transparent
            type="button"
            color="secondary"
            className={`btn--icon rounded${
              editor.isActive("underline") ? " text-dark" : ""
            }`}
            onClick={toggleUnderline}
            aria-label="Understreget"
          >
            <FormatUnderlineIcon />
          </Button>
          <Button
            transparent
            type="button"
            color="secondary"
            className={`btn--icon rounded${
              editor.isActive("italic") ? " text-dark" : ""
            }`}
            onClick={toggleItalic}
            aria-label="Kursiv"
          >
            <FormatItalicIcon />
          </Button>
          <Button
            transparent
            type="button"
            color="secondary"
            className={`btn--icon rounded${
              editor.isActive("strike") ? " text-dark" : ""
            }`}
            onClick={toggleStrike}
            aria-label="Udstreget"
          >
            <FormatStrikethroughIcon />
          </Button>
        </div>
        <span className="separator"></span>
        <div
          className="btn-group rounded-0 px-2"
          role="group"
          aria-label="Tredje gruppe"
        >
          <LinkDropdown editor={editor} />
          <KodeTagDropdown
            emailType={emailType}
            onKodeTagSelect={(kodetag) => {
              addCodeTag(kodetag);
            }}
          />{" "}
        </div>
      </div>
      <EditorContent className="l-email-editor" editor={editor} />
    </>
  );
}
